import { useEffect, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, CardBody, CardHeader, Input, Sheet, Spinner, Stub, Toast } from "@/components/ui"
import { ShareModeControl, shareModeLabel } from "@/components/connector-share"
import type { ConnectorOut, ConnectorSpecOut, TeamOut, TelegramLinkOut } from "@/lib/types"
import { CheckCircle2Icon, KeyRoundIcon, MonitorIcon, PlugIcon, SendIcon, Trash2Icon, UsersIcon } from "lucide-react"

export const Route = createFileRoute("/_app/connectors")({
  component: ConnectorsPage,
})

// Auth-model groupings — keep all branching here so the tile only
// has to ask "which bucket is this connector in?".
const isOAuth = (m: string) => m === "oauth_google" || m === "oauth_microsoft"
const isLocal = (m: string) => m === "client_local"
const isApiKey = (m: string) => m === "api_key" || m === "custom"
const isBotLink = (m: string) => m === "bot_link"

function ConnectorsPage() {
  const me = auth.me()
  const t = useT()
  const qc = useQueryClient()
  const specs = useQuery({
    queryKey: ["connector-specs"],
    queryFn: () => api<ConnectorSpecOut[]>("/api/dashboard/connectors/specs"),
  })
  // Own rows + the ones teammates shared (those carry `shared_from`).
  const all = useQuery({
    queryKey: ["connectors", me?.id],
    queryFn: () => api<ConnectorOut[]>("/api/dashboard/connectors", {
      query: { user_id: me?.id },
    }),
    enabled: !!me?.id,
  })
  const team = useQuery({ queryKey: ["team"], queryFn: () => api<TeamOut>("/api/team"), enabled: !!me?.id })
  const inWorkspace = !!team.data?.workspace
  const shareable = new Set(team.data?.shareable_kinds ?? [])
  const mine = { ...all, data: all.data?.filter((c) => !c.shared_from) }
  const fromTeam = (all.data ?? []).filter((c) => !!c.shared_from)
  const [shareError, setShareError] = useState<string | null>(null)

  const del = useMutation({
    mutationFn: (id: string) => api(`/api/dashboard/connectors/${id}`, {
      method: "DELETE",
      query: { user_id: me?.id },
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  })

  const [oauthError, setOauthError] = useState<string | null>(null)
  const [inboundFor, setInboundFor] = useState<ConnectorOut | null>(null)
  const [oauthBusy, setOauthBusy] = useState<string | null>(null)
  const [connectedMsg, setConnectedMsg] = useState<string | null>(null)

  // The OAuth callback redirects back here with ?connected=<kind> after a
  // successful link. Show a confirmation, drop the param so a refresh doesn't
  // replay it, and refresh the connected list.
  useEffect(() => {
    const k = new URLSearchParams(window.location.search).get("connected")
    if (!k) return
    setConnectedMsg(`${k} connected.`)
    window.history.replaceState({}, "", window.location.pathname)
    qc.invalidateQueries({ queryKey: ["connectors"] })
  }, [qc])

  const startOAuth = async (kind: string) => {
    setOauthError(null)
    setOauthBusy(kind)
    try {
      const out = await api<{ url: string }>(
        `/api/dashboard/connectors/${kind}/oauth/start`,
        { method: "POST", query: { user_id: me?.id } },
      )
      window.location.href = out.url
    } catch (e) {
      // The OAuth start fails loudly when Google credentials aren't set
      // server-side (503) — surface it instead of silently doing nothing.
      const err = e as { status?: number; detail?: string }
      setOauthError(
        err.detail ||
          (err.status === 503
            ? "OAuth isn't configured on the server yet (missing GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET)."
            : "Couldn't start the connection. Try again."),
      )
      setOauthBusy(null)
    }
  }

  const connectedKinds = new Set((mine.data || []).map((c) => c.kind))
  const visibleSpecs = (specs.data || []).filter((s) => s.enabled)
  const grouped = visibleSpecs.reduce<Record<string, ConnectorSpecOut[]>>((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s); return acc
  }, {})

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Connectors</h1>
        <p className="text-[13px] text-muted-foreground mt-1.5">
          Plug Lucy into your tools. OAuth where possible — Apple-native services need the Mac app.
        </p>
      </div>

      {connectedMsg && <Toast variant="ok">{connectedMsg}</Toast>}
      {oauthError && <Toast variant="destructive">{oauthError}</Toast>}
      {shareError && <Toast variant="destructive">{shareError}</Toast>}

      {(specs.isLoading || all.isLoading) && <Spinner />}

      {/* Active first */}
      <Card>
        <CardHeader title="Connected" subtitle={`${mine.data?.length ?? 0} active`} />
        <CardBody>
          {mine.data?.length === 0 && <Stub title="No connectors yet" hint="Pick from the list below." />}
          <div className="grid gap-2">
            {mine.data?.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-foreground/[0.04] p-3.5">
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle2Icon className="h-4 w-4 text-[hsl(var(--ok))]" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.account_label || c.kind}</div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                      <Badge variant={c.status === "active" ? "ok" : "warn"}>{c.status}</Badge>
                      <span className="truncate">{c.kind}</span>
                    </div>
                  </div>
                </div>
                {inWorkspace && shareable.has(c.kind) && (
                  <ShareModeControl connectorId={c.id} mode={c.share_mode} onError={(e) => setShareError((e as Error).message)} />
                )}
                {["imessage", "signal", "whatsapp", "slack"].includes(c.kind) && (
                  <Button variant="outline" size="sm" onClick={() => setInboundFor(c)}>
                    {c.settings?.inbound_enabled || c.settings?.allow_team_dms ? "Inbound: on" : "Inbound"}
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => {
                  if (window.confirm(`Disconnect ${c.kind}?`)) del.mutate(c.id)
                }}>
                  <Trash2Icon className="h-4 w-4 text-muted-foreground" />
                </Button>
              </div>
            ))}
          </div>
        </CardBody>
      </Card>

      {fromTeam.length > 0 && (
        <Card>
          <CardHeader title={t("team.conn.fromTeam")} subtitle={t("team.conn.fromTeamSub")} />
          <CardBody>
            <div className="grid gap-2" data-testid="connectors-from-team">
              {fromTeam.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-border bg-foreground/[0.04] p-3.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <UsersIcon className="h-4 w-4 text-muted-foreground" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.account_label || c.kind}</div>
                      <div className="text-[10px] text-muted-foreground flex items-center gap-2">
                        <span className="truncate">{c.kind}</span>
                        <span>·</span>
                        <span className="truncate">{t("team.conn.by", { name: c.shared_from?.name ?? "…" })}</span>
                      </div>
                    </div>
                  </div>
                  <Badge variant={c.share_mode === "full" ? "ok" : "muted"}>{shareModeLabel(t, c.share_mode)}</Badge>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      <InboundSheet conn={inboundFor} onClose={() => { setInboundFor(null); qc.invalidateQueries({ queryKey: ["connectors"] }) }} />

      {Object.entries(grouped).map(([cat, list]) => (
        <Card key={cat}>
          <CardHeader title={cat} subtitle={`${list.length} available`} />
          <CardBody>
            <div className="grid sm:grid-cols-2 gap-2">
              {list.map((s) => (
                <ConnectorTile
                  key={s.kind}
                  s={s}
                  connected={connectedKinds.has(s.kind)}
                  onOAuth={() => startOAuth(s.kind)}
                  oauthBusy={oauthBusy === s.kind}
                />
              ))}
            </div>
          </CardBody>
        </Card>
      ))}
    </div>
  )
}

function ConnectorTile({
  s, connected, onOAuth, oauthBusy,
}: { s: ConnectorSpecOut; connected: boolean; onOAuth: () => void; oauthBusy?: boolean }) {
  const [showKey, setShowKey] = useState(false)
  const local = isLocal(s.auth_model)
  return (
    <div className="flex items-start justify-between gap-3 rounded-xl border border-border bg-foreground/[0.04] p-3.5">
      <div className="flex items-start gap-3 min-w-0">
        <PlugIcon className="h-4 w-4 text-muted-foreground mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{s.display_name}</div>
          {s.description && <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{s.description}</div>}
          <div className="mt-1.5 flex items-center gap-1.5">
            <Badge variant="muted">{s.auth_model}</Badge>
            {connected && <Badge variant="ok">connected</Badge>}
          </div>
        </div>
      </div>
      <div className="shrink-0">
        {local ? (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1.5">
            <MonitorIcon className="h-3 w-3" /> Mac app required
          </div>
        ) : isOAuth(s.auth_model) ? (
          <Button size="sm" variant="outline" onClick={onOAuth} disabled={oauthBusy}>
            {oauthBusy ? <Spinner /> : null}Connect
          </Button>
        ) : isBotLink(s.auth_model) ? (
          <TelegramConnectButton connected={connected} />
        ) : isApiKey(s.auth_model) ? (
          <Button size="sm" variant="outline" onClick={() => setShowKey(true)}>
            <KeyRoundIcon className="h-3 w-3" /> Key
          </Button>
        ) : null}
      </div>
      {isApiKey(s.auth_model) && (
        <APIKeySheet
          open={showKey}
          onClose={() => setShowKey(false)}
          kind={s.kind}
          label={s.display_name}
          instructions={s.setup_instructions}
        />
      )}
    </div>
  )
}

function TelegramConnectButton({ connected }: { connected: boolean }) {
  const me = auth.me()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function start() {
    setBusy(true); setErr(null)
    try {
      const out = await api<TelegramLinkOut>(
        "/api/dashboard/connectors/telegram/start-link",
        { method: "POST", query: { user_id: me?.id } },
      )
      // Open Telegram in a new tab — user taps Start, webhook binds the chat.
      window.open(out.deep_link, "_blank", "noopener,noreferrer")
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button size="sm" variant="outline" onClick={start} disabled={busy}>
        {busy ? <Spinner /> : <SendIcon className="h-3 w-3" />}
        {connected ? "Re-link" : "Connect"}
      </Button>
      {err && <span className="text-[10px] text-destructive max-w-[200px]">{err}</span>}
    </div>
  )
}

/** Connectors whose "key" is really several values. Anything not listed
 *  here gets the single api_key box. Field names match what the backend
 *  connector module reads from the encrypted auth payload. */
const MULTI_FIELDS: Record<string, { key: string; label: string; placeholder: string; secret?: boolean; help?: string }[]> = {
  whatsapp: [
    { key: "token", label: "Access token", placeholder: "EAAG…", secret: true,
      help: "Meta for Developers → your app → WhatsApp → API Setup. Use a permanent System User token with whatsapp_business_messaging + whatsapp_business_management." },
    { key: "phone_number_id", label: "Phone number ID", placeholder: "1234567890123", help: "Shown under the phone number in API Setup (not the phone number itself)." },
  ],
  signal: [
    { key: "endpoint", label: "signal-cli REST endpoint", placeholder: "http://127.0.0.1:8080", help: "The signal-cli-rest-api instance the Mac agent can reach." },
    { key: "account", label: "Account number", placeholder: "+30691234567" },
  ],
}

function APIKeySheet({
  open, onClose, kind, label, instructions,
}: { open: boolean; onClose: () => void; kind: string; label: string; instructions?: string }) {
  const me = auth.me()
  const qc = useQueryClient()
  const multi = MULTI_FIELDS[kind]
  const [key, setKey] = useState("")
  const [values, setValues] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const ready = multi ? multi.every((f) => (values[f.key] || "").trim()) : !!key.trim()
  async function save() {
    setBusy(true); setErr(null)
    try {
      // Backend expects { fields: { ... } }: one api_key for most
      // connectors, a per-kind set of fields for the ones in MULTI_FIELDS.
      const fields = multi
        ? Object.fromEntries(multi.map((f) => [f.key, (values[f.key] || "").trim()]))
        : { api_key: key }
      await api(`/api/dashboard/connectors/${kind}/connect-api-key`, {
        method: "POST",
        body: { fields },
        query: { user_id: me?.id },
      })
      qc.invalidateQueries({ queryKey: ["connectors"] })
      setKey(""); setValues({})
      onClose()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Connect ${label}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || !ready}>{busy ? <Spinner /> : null}Connect</Button>
        </>
      }
    >
      <div className="space-y-3">
        {instructions && (
          <p className="text-xs text-muted-foreground whitespace-pre-line">{instructions}</p>
        )}
        {multi ? multi.map((f) => (
          <div key={f.key} className="space-y-1">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{f.label}</label>
            <Input type={f.secret ? "password" : "text"} placeholder={f.placeholder} value={values[f.key] || ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))} className="font-mono" />
            {f.help && <p className="text-[11px] text-muted-foreground">{f.help}</p>}
          </div>
        )) : (
          <Input type="password" placeholder="sk-…" value={key} onChange={(e) => setKey(e.target.value)} />
        )}
        {err && <Toast variant="destructive">{err}</Toast>}
      </div>
    </Sheet>
  )
}


/** Inbound settings for channels Lucy can answer on (iMessage / Signal via
 *  the Mac agent, WhatsApp webhook, Slack events). */
function InboundSheet({ conn, onClose }: { conn: ConnectorOut | null; onClose: () => void }) {
  const [enabled, setEnabled] = useState(false)
  const [owner, setOwner] = useState("")
  const [senders, setSenders] = useState("")
  const [anyone, setAnyone] = useState(false)
  const [teamDms, setTeamDms] = useState(false)
  // WhatsApp outbound: notifications to the owner number; a Meta-approved
  // template carries them outside the 24-hour window.
  const [notify, setNotify] = useState(false)
  const [template, setTemplate] = useState("")
  const [templateLang, setTemplateLang] = useState("el")
  const [wabaId, setWabaId] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [tpl, setTpl] = useState<{ name: string; status: string; rejected_reason?: string | null } | null>(null)
  const [tplBusy, setTplBusy] = useState(false)
  const [hook, setHook] = useState<{ subscribed_before: boolean; subscribed_now: boolean; apps: string[] } | null>(null)
  async function webhookCheck() {
    if (!conn) return
    setTplBusy(true); setErr(null)
    try {
      const out = await api<{ subscribed_before: boolean; subscribed_now: boolean; apps: string[] }>(
        `/api/dashboard/connectors/${conn.id}/whatsapp/webhook-check`,
        { method: "POST", body: { waba_id: wabaId.trim() || undefined } },
      )
      setHook(out)
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setTplBusy(false)
    }
  }
  async function templateAction(kind: "create" | "status") {
    if (!conn) return
    setTplBusy(true); setErr(null)
    try {
      const body = { name: template.trim() || undefined, lang: templateLang.trim() || undefined, waba_id: wabaId.trim() || undefined }
      const out = await api<{ name: string; lang: string; status: string; rejected_reason?: string | null }>(
        `/api/dashboard/connectors/${conn.id}/whatsapp/template`,
        kind === "create" ? { method: "POST", body } : { method: "GET" },
      )
      setTpl(out); setTemplate(out.name); setTemplateLang(out.lang); if (kind === "create") setNotify(true)
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setTplBusy(false)
    }
  }
  useEffect(() => {
    if (!conn) return
    setTpl(null); setHook(null)
    const st = (conn.settings || {}) as Record<string, unknown>
    setEnabled(!!st.inbound_enabled); setOwner(String(st.owner_number || ""))
    setSenders(Array.isArray(st.allowed_senders) ? (st.allowed_senders as string[]).join(", ") : "")
    setAnyone(!!st.allow_anyone); setTeamDms(!!st.allow_team_dms); setErr(null)
    setNotify(!!st.notify_enabled); setTemplate(String(st.notify_template || "")); setTemplateLang(String(st.notify_template_lang || "el")); setWabaId(String(st.waba_id || ""))
  }, [conn])
  if (!conn) return null
  const isSlack = conn.kind === "slack"
  async function save() {
    if (!conn) return
    setBusy(true); setErr(null)
    try {
      const settings: Record<string, unknown> = isSlack
        ? { allow_team_dms: teamDms }
        : { inbound_enabled: enabled, owner_number: owner.trim(), allow_anyone: anyone,
            allowed_senders: senders.split(",").map((x) => x.trim()).filter(Boolean),
            ...(conn.kind === "whatsapp" ? { notify_enabled: notify, notify_template: template.trim(), notify_template_lang: templateLang.trim() || "el", waba_id: wabaId.trim() } : {}) }
      await api(`/api/dashboard/connectors/${conn.id}/settings`, { method: "PATCH", body: { settings } })
      onClose()
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const hint = conn.kind === "imessage"
    ? "Needs the Lucy Mac app open with Full Disk Access (reads chat.db) and Automation permission for Messages (sends). Message yourself from your iPhone, or add the handles Lucy should answer."
    : conn.kind === "signal"
    ? "Needs your signal-cli endpoint configured on the connector. Lucy answers only the numbers listed here."
    : conn.kind === "whatsapp"
    ? "Needs WHATSAPP_VERIFY_TOKEN / WHATSAPP_APP_SECRET on the server and the webhook set in the Meta app. Lucy answers only the numbers listed here."
    : "By default only your own Slack user can talk to Lucy. Allowing team DMs lets any member of the workspace spend your budget."
  return (
    <Sheet open={!!conn} onClose={onClose} title={`Inbound · ${conn.kind}`}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>{busy ? <Spinner /> : null}Save</Button></>}>
      <div className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">{hint}</p>
        {isSlack ? (
          <label className="flex items-center gap-2"><input type="checkbox" checked={teamDms} onChange={(e) => setTeamDms(e.target.checked)} /> Allow DMs from anyone in the workspace</label>
        ) : (
          <>
            <label className="flex items-center gap-2"><input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Lucy answers messages on this channel</label>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Your number / handle</label>
              <Input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="+30691234567 or you@icloud.com" className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Other allowed senders (comma separated)</label>
              <Input value={senders} onChange={(e) => setSenders(e.target.value)} placeholder="+30691111111, friend@icloud.com" className="font-mono" />
            </div>
            <label className="flex items-center gap-2 text-xs text-warn"><input type="checkbox" checked={anyone} onChange={(e) => setAnyone(e.target.checked)} /> Answer anyone (not recommended: strangers can spend your budget)</label>
            {conn.kind === "whatsapp" && (
              <div className="space-y-2 rounded-xl bg-secondary/60 p-3 ring-1 ring-inset ring-border">
                <label className="flex items-center gap-2"><input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} /> Send me reminders, briefs and approval buttons here</label>
                <p className="text-[11px] text-muted-foreground">Inside 24 h of your last message Lucy sends normal texts and Approve/Deny buttons. Outside that window Meta only allows an approved template. Lucy can create one for you (body “Lucy: {"{{1}}"}”, category Utility); Meta reviews it, usually within minutes.</p>
                <div className="flex gap-2">
                  <Input value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="lucy_notification" className="font-mono" />
                  <Input value={templateLang} onChange={(e) => setTemplateLang(e.target.value)} placeholder="el" className="w-20 font-mono" />
                </div>
                <div className="space-y-1">
                  <label className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">WhatsApp Business Account ID</label>
                  <Input value={wabaId} onChange={(e) => setWabaId(e.target.value)} placeholder="1841753446995898" className="font-mono" />
                  <p className="text-[11px] text-muted-foreground">Meta → WhatsApp → API Setup. Only needed if “Create template” says it cannot find the account.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" disabled={tplBusy} onClick={() => void templateAction("create")}>{tplBusy ? <Spinner /> : null} Create template for me</Button>
                  <Button size="sm" variant="ghost" disabled={tplBusy} onClick={() => void templateAction("status")}>Check status</Button>
                  <Button size="sm" variant="ghost" disabled={tplBusy} onClick={() => void webhookCheck()}>Check webhook</Button>
                  {hook && (
                    <Badge variant={hook.subscribed_now ? "ok" : "destructive"}>
                      {hook.subscribed_now ? (hook.subscribed_before ? "webhook subscribed" : "webhook fixed: app subscribed") : "not subscribed"}{hook.apps.length ? ` · ${hook.apps.join(", ")}` : ""}
                    </Badge>
                  )}
                  {tpl && (
                    <Badge variant={tpl.status === "APPROVED" ? "ok" : tpl.status === "REJECTED" ? "destructive" : "muted"}>
                      {tpl.name} · {tpl.status.toLowerCase()}{tpl.rejected_reason ? ` · ${tpl.rejected_reason}` : ""}
                    </Badge>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {err && <Toast variant="destructive">{err}</Toast>}
      </div>
    </Sheet>
  )
}
